import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

gsap.registerPlugin(ScrollTrigger);

const { FiArrowUpRight, FiLinkedin, FiMail, FiCheck, FiSend } = FiIcons;
const initialForm = { name: '', email: '', organization: '', message: '', website: '' };

const FieldError = ({ id, children }) => children ? <p id={id} className="mt-2 text-sm font-bold text-[#7a1f1f]" role="alert">{children}</p> : null;

const ConnectSection = () => {
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const formRef = useRef(null);
  const footerRef = useRef(null);
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState('');
  const isLocalReview = import.meta.env.DEV;
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(titleRef.current.children, {
        scrollTrigger: { trigger: titleRef.current, start: 'top 80%' },
        y: 100, opacity: 0, duration: 1, stagger: 0.1, ease: 'power3.out'
      });
      gsap.from(formRef.current, {
        scrollTrigger: { trigger: formRef.current, start: 'top 75%' },
        y: 50, opacity: 0, duration: 1, delay: 0.2, ease: 'power3.out'
      });
      gsap.from(footerRef.current.children, {
        scrollTrigger: { trigger: footerRef.current, start: 'top 85%' },
        y: 30, opacity: 0, duration: 0.8, stagger: 0.1, ease: 'power2.out'
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (isLocalReview || !turnstileSiteKey) return undefined;
    window.onKwbTurnstileSuccess = (token) => setTurnstileToken(token);
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      delete window.onKwbTurnstileSuccess;
      script.remove();
    };
  }, [isLocalReview, turnstileSiteKey]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    if (status !== 'idle') {
      setStatus('idle');
      setMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('sending');
    setMessage('');
    setErrors({});

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-contact-key': formData.email.trim().toLowerCase() || 'anonymous' },
        body: JSON.stringify({ ...formData, turnstileToken }),
      });
      const result = await response.json();
      if (!response.ok) {
        setErrors(result.errors || {});
        throw new Error(result.message || 'Your message could not be submitted.');
      }
      setStatus('success');
      setMessage(result.message);
      setFormData(initialForm);
      setTurnstileToken('');
      window.turnstile?.reset?.();
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Your message could not be submitted.');
    }
  };

  const inputClass = 'w-full bg-transparent border-b border-black px-0 py-4 text-lg font-medium placeholder:text-[#777] focus:outline-none focus:border-b-[3px] focus:border-[#918147] transition-all';

  return (
    <div id="contact" ref={containerRef} className="relative bg-[#F5F5F5] text-black min-h-screen flex flex-col pt-24 md:pt-32 pb-8 px-4 md:px-8 overflow-hidden">
      <div className="w-full flex justify-center mb-8 md:mb-16">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-gray-400">Connect / 5</span>
      </div>
      <div className="w-full text-center mb-4">
        <span className="text-xs font-bold tracking-widest uppercase text-black">Ideas become impact through execution</span>
      </div>

      <div ref={titleRef} className="w-full flex flex-col items-center justify-center text-center mb-16 md:mb-24">
        <h2 className="text-[9vw] md:text-[6.5vw] font-black tracking-tighter leading-[0.9] uppercase overflow-hidden"><span className="inline-block">Start a</span></h2>
        <div className="flex items-center gap-4 my-2 md:my-4 w-full justify-center">
          <div className="h-px bg-black w-12 md:w-24" />
          <span className="text-xs md:text-sm font-bold tracking-[0.5em] uppercase">Meaningful</span>
          <div className="h-px bg-black w-12 md:w-24" />
        </div>
        <h2 className="text-[9vw] md:text-[6.5vw] font-black tracking-tighter leading-[0.9] uppercase overflow-hidden"><span className="inline-block">Conversation</span></h2>
      </div>

      <section ref={formRef} aria-labelledby="contact-form-heading" className="w-full max-w-6xl mx-auto mb-28 md:mb-36 relative z-10 grid grid-cols-1 lg:grid-cols-12 border border-black bg-white">
        <div className="lg:col-span-4 bg-black text-white p-7 md:p-10 flex flex-col">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#e2c96e]">Get in touch</div>
          <h3 id="contact-form-heading" className="mt-6 text-4xl md:text-5xl font-black uppercase tracking-tight leading-[0.92]">Send a note</h3>
          <p className="mt-7 text-base md:text-lg leading-relaxed text-[#d8d8d8]">Have a question, an idea, or a conversation worth having? I’d be glad to hear from you.</p>
          <div className="mt-10 space-y-4 text-sm font-bold uppercase tracking-wider">
            <a href="mailto:kwb@kwbotrous.com" className="flex items-center gap-3 hover:text-[#e2c96e] transition-colors"><SafeIcon icon={FiMail} /> kwb@kwbotrous.com</a>
            <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-[#e2c96e] transition-colors"><SafeIcon icon={FiLinkedin} /> LinkedIn</a>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate className="lg:col-span-8 p-7 md:p-10 lg:p-12" aria-describedby="form-privacy-note">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
            <div>
              <label htmlFor="contact-name" className="text-xs font-black uppercase tracking-[0.16em]">Name *</label>
              <input id="contact-name" name="name" value={formData.name} onChange={handleChange} autoComplete="name" maxLength="100" required className={inputClass} placeholder="Your name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} />
              <FieldError id="name-error">{errors.name}</FieldError>
            </div>
            <div>
              <label htmlFor="contact-email" className="text-xs font-black uppercase tracking-[0.16em]">Email *</label>
              <input id="contact-email" name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" maxLength="254" required className={inputClass} placeholder="you@company.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} />
              <FieldError id="email-error">{errors.email}</FieldError>
            </div>
            <div>
              <label htmlFor="contact-organization" className="text-xs font-black uppercase tracking-[0.16em]">Organization <span className="font-medium text-gray-500">— optional</span></label>
              <input id="contact-organization" name="organization" value={formData.organization} onChange={handleChange} autoComplete="organization" maxLength="120" className={inputClass} placeholder="Company or organization" aria-invalid={Boolean(errors.organization)} aria-describedby={errors.organization ? 'organization-error' : undefined} />
              <FieldError id="organization-error">{errors.organization}</FieldError>
            </div>
          </div>

          <div className="mt-10">
            <div className="flex items-end justify-between gap-4">
              <label htmlFor="contact-message" className="text-xs font-black uppercase tracking-[0.16em]">Message *</label>
              <span className="text-xs text-gray-500" aria-live="polite">{formData.message.length} / 3,000</span>
            </div>
            <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} minLength="20" maxLength="3000" required rows="6" className={`${inputClass} resize-y leading-relaxed`} placeholder="Tell me a little about what’s on your mind." aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : 'form-privacy-note'} />
            <FieldError id="message-error">{errors.message}</FieldError>
          </div>

          <div className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
            <label htmlFor="contact-website">Website</label>
            <input id="contact-website" name="website" value={formData.website} onChange={handleChange} tabIndex="-1" autoComplete="off" />
          </div>

          {!isLocalReview && turnstileSiteKey && <div className="mt-8 cf-turnstile" data-sitekey={turnstileSiteKey} data-action="contact" data-callback="onKwbTurnstileSuccess" />}

          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-black pt-7">
            <p id="form-privacy-note" className="max-w-md text-sm leading-relaxed text-[#555]">Your information is used only to respond to this inquiry. It is not added to a marketing list. Please do not include confidential information.</p>
            <button type="submit" disabled={status === 'sending'} className="group min-h-14 inline-flex items-center justify-center gap-3 bg-[#e2c96e] text-black px-7 py-4 text-sm font-black uppercase tracking-[0.15em] hover:bg-black hover:text-white focus:outline-none focus:ring-4 focus:ring-[#918147]/40 disabled:opacity-60 disabled:cursor-wait transition-colors">
              {status === 'sending' ? 'Sending…' : 'Send message'} <SafeIcon icon={FiSend} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {message && <div className={`mt-6 p-5 flex items-start gap-3 border ${status === 'success' ? 'bg-[#eef4e7] border-[#56703b]' : 'bg-[#fff1f1] border-[#7a1f1f]'}`} role={status === 'error' ? 'alert' : 'status'} aria-live="polite">
            {status === 'success' && <SafeIcon icon={FiCheck} className="mt-0.5 text-[#48602f]" />}
            <div><strong>{status === 'success' ? 'Submission complete.' : 'Please check the form.'}</strong><p className="mt-1">{message}</p>{status === 'error' && <a href="mailto:kwb@kwbotrous.com" className="mt-2 inline-block font-bold underline">Email Katherine directly</a>}</div>
          </div>}
        </form>
      </section>

      <div ref={footerRef} className="mt-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-3 flex flex-col gap-4 text-sm font-bold uppercase tracking-wider">
            <a href="#blog" className="hover:opacity-60 transition-opacity">Blog</a>
            <a href="/resume.pdf" className="hover:opacity-60 transition-opacity">Resume</a>
            <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="hover:opacity-60 transition-opacity">LinkedIn</a>
          </div>
          <div className="md:col-span-4" />
          <div className="md:col-span-5 text-right flex flex-col items-start md:items-end gap-6">
            <div className="text-2xl md:text-4xl font-black tracking-tighter">Oklahoma</div>
            <a href="mailto:kwb@kwbotrous.com" className="text-lg md:text-2xl font-black tracking-tighter break-all md:break-normal hover:opacity-60 transition-opacity">kwb@kwbotrous.com</a>
            <div className="flex gap-8 mt-4 text-xs font-bold uppercase tracking-widest">
              <a href="https://www.linkedin.com/in/kathy-william-botrous" target="_blank" rel="noreferrer" className="flex items-center gap-1 border-b border-black pb-0.5 hover:opacity-60 transition-opacity">LinkedIn <SafeIcon icon={FiArrowUpRight} /></a>
            </div>
          </div>
        </div>
        <div className="w-full border-t border-black pt-6 md:pt-8">
          <p id="footer-name" className="font-black uppercase tracking-[-0.055em] text-center select-none leading-[0.9] text-[clamp(2.35rem,6.4vw,6.75rem)]"><span className="block md:inline">Katherine</span>{' '}<span className="block md:inline whitespace-nowrap">William-Botrous</span></p>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center mt-8 text-xs font-bold uppercase tracking-wider text-gray-500 gap-4">
          <div>CT (Central Time)</div><div>Sales • Operations • Leadership</div><div className="text-right">© 2026 Katherine William-Botrous</div>
        </div>
      </div>
    </div>
  );
};

export default ConnectSection;
